// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// ASP #32 — who works this clinical review, and moving it to someone else. The server checks the
// person works at this pharmacy, records the move (from → to) in the audit trail, and notifies the
// new pharmacist in-app (no patient details in the notification).

import { useState } from 'react'
import SearchableSelect from '../../components/common/SearchableSelect'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import { useAssignReview, useReviewAssignees } from '../../hooks/queries/carerxClinical'
import type { AssignContext } from '../../api/carerxClinical'

const link = { fontSize: 11.5, fontWeight: 600, color: '#185FA5', background: 'none', border: 'none', padding: 0, cursor: 'pointer' } as const

export default function ReviewAssignee({ therapyId, rphId, rphName, assignedAt, onSelf, onChanged, keepStatus, context }: {
  therapyId: string
  rphId: string | null
  rphName: string | null
  assignedAt: string | null
  /** The existing one-click Self-assign. */
  onSelf: () => void
  onChanged: (message: string) => void
  /** From the enrollment queue: name the pharmacist without starting the review. */
  keepStatus?: boolean
  /** ASP #27/#28: where this control sits — the new pharmacist's notification names it. */
  context?: AssignContext
}) {
  const [picking, setPicking] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const users = useReviewAssignees(picking)
  const assign = useAssignReview()
  const choose = (id: string) => {
    if (!id || id === rphId) { setPicking(false); return }
    setErr(null)
    assign.mutate({ therapyId, rphId: id, keepStatus, context }, {
      onSuccess: r => { setPicking(false); onChanged(`Assigned to ${r.rph_name || 'the pharmacist'}.`) },
      onError: e => setErr(friendlyError(e)),
    })
  }
  return (
    <div style={{ minWidth: 150 }}>
      {rphId ? (
        <span>{rphName}{assignedAt && <span style={{ display: 'block', fontSize: 10, color: '#8496a5' }}>Assigned {formatUsDate(assignedAt)}</span>}</span>
      ) : (
        <button style={{ ...link, fontSize: 12 }} onClick={onSelf}>Self-assign</button>
      )}
      {!picking ? (
        <button style={{ ...link, display: 'block', marginTop: 2 }} onClick={() => setPicking(true)}>{rphId ? 'Reassign…' : 'Assign to…'}</button>
      ) : (
        <div style={{ marginTop: 4, width: 190 }}>
          {users.isLoading ? <span style={{ fontSize: 11.5, color: '#8496a5' }}>Loading…</span> : (
            <SearchableSelect ariaLabel="Assign to" value="" placeholder="Choose a pharmacist…" onChange={choose}
              options={(users.data ?? []).filter(u => u.id !== rphId).map(u => ({ value: u.id, label: u.display_name }))} />
          )}
          <button style={{ ...link, marginTop: 2, color: '#8496a5' }} onClick={() => setPicking(false)}>Cancel</button>
        </div>
      )}
      {err && <div role="alert" style={{ fontSize: 11, color: '#b23a48' }}>{err}</div>}
    </div>
  )
}
